import React from 'react';
import { Divider, Logs, Typography, Wrapper } from '@screentone/core';

import { PreferredTimezone } from 'data/generated/graphql';
import { formatInTimeZone } from 'utils/dates';

interface PublishAttempt {
  httpStatus: number;
  page: {
    publishUtc?: string | null;
    revisorUser?: string;
    publishVersion?: string;
    updatedUtc?: string;
    triggeredBy?:
      | {
          contentId?: string | null;
          collectionId?: string | null;
          user?: string | null;
          datetimeUtc?: string | null;
        }
      | string
      | null;
  };
  error?: {
    message?: string | null;
    errorDetailsRaw?: string | null;
  } | null;
}

interface PageAnalyticsProps {
  published: PublishAttempt[];
  preferredTimezone?: PreferredTimezone;
}

const PageAnalytics: React.FC<PageAnalyticsProps> = ({ published, preferredTimezone }) => {
  if (published.length === 0) {
    return null;
  }

  // Group publish attempts by date
  const groupedByDate: Record<string, Array<{ attempt: PublishAttempt; timeOnly: string }>> = {};

  published.forEach((attempt) => {
    if (!attempt.page.publishUtc || !preferredTimezone) {
      return;
    }

    try {
      const date = new Date(attempt.page.publishUtc);
      const dateKey = formatInTimeZone(date, preferredTimezone, 'MMM dd, yyyy');
      const timeOnly = formatInTimeZone(date, preferredTimezone, 'h:mm:ss a zzz');

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!groupedByDate[dateKey]) {
        groupedByDate[dateKey] = [];
      }

      groupedByDate[dateKey].push({
        attempt,
        timeOnly
      });
    } catch {
      // Skip entries with invalid dates
    }
  });

  return (
    <Wrapper margin={{ bottom: 'lg' }}>
      <details>
        <summary>
          <Typography inline variant="header4">{`See Recent Publish Attempts* (Last ${published.length})`}</Typography>
          <Typography variant="note">
            *Only includes publish attempts that pass validation and are sent to Allesseh
          </Typography>
        </summary>
        <Logs>
          {Object.entries(groupedByDate).map(([dateKey, items]) => (
            <Logs.Group key={dateKey} timestamp={dateKey}>
              {items.map(({ attempt, timeOnly }, index) => {
                const statusEmoji = attempt.httpStatus === 200 || attempt.httpStatus === 202 ? '✅' : '❌';
                let summary = `%USER% published version ${attempt.page.publishVersion ?? 'Unknown'} ${statusEmoji}`;

                if (attempt.page.triggeredBy && typeof attempt.page.triggeredBy === 'object') {
                  const { triggeredBy } = attempt.page;
                  let triggerInfo = ' (Triggered by ';

                  if (triggeredBy.user) {
                    triggerInfo += `user: ${triggeredBy.user}`;
                  }
                  if (triggeredBy.contentId) {
                    if (triggerInfo.length > 14) triggerInfo += ', ';
                    triggerInfo += `contentId: ${triggeredBy.contentId}`;
                  }
                  if (triggeredBy.collectionId) {
                    if (triggerInfo.length > 14) triggerInfo += ', ';
                    triggerInfo += `collectionId: ${triggeredBy.collectionId}`;
                  }
                  if (triggeredBy.datetimeUtc) {
                    if (triggerInfo.length > 14) triggerInfo += ', ';
                    triggerInfo += `datetimeUtc: ${triggeredBy.datetimeUtc}`;
                  }
                  triggerInfo += ')';
                  summary += triggerInfo;
                }

                return (
                  <Logs.Item
                    key={`${attempt.page.publishVersion}-${index}`}
                    timestamp={timeOnly}
                    user={attempt.page.revisorUser ?? 'Unknown'}
                    summary={summary}
                  />
                );
              })}
            </Logs.Group>
          ))}
        </Logs>
      </details>
      <Divider />
    </Wrapper>
  );
};

export default PageAnalytics;
